import {
  Button,
  cn,
  Input,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';

import { Plus } from 'lucide-react';

import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import { newUuid } from '@shared';

import {
  selectSessionAdvisor,
  selectAdvisorUpdatesStatus,
  putAdvisorReferralSourceByAdvisorIdRequest,
} from '@client/model';

interface SelectReferralSourceProps {
  selectedReferralSourceId: string | undefined;
  onChangeReferralSourceId: (value: string | undefined) => void;
  className?: string;
}

export function SelectReferralSource({
  selectedReferralSourceId,
  onChangeReferralSourceId,
  className,
}: SelectReferralSourceProps) {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const status = useSelector(selectAdvisorUpdatesStatus).putAdvisorReferralSource;

  const isLoading = status === 'loading';

  const [isAddingMode, setIsAddingMode] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');

  const [referralSources, setReferralSources] = useState<AdvisorReferralSource[]>(
    _.sortBy(
      advisor.referralSources?.map(source => ({
        referralSourceLabel: source.referralSourceLabel,
        advisorReferralSourceId: source.advisorReferralSourceId,
      })),
      source => source.referralSourceLabel.toLowerCase().trim(),
    ) ?? [],
  );

  const selectedSource = referralSources.find(
    source => source.advisorReferralSourceId === selectedReferralSourceId,
  );

  const handleValueChange = useCallback(
    (value: string) => {
      if (value === 'undefined') {
        onChangeReferralSourceId(undefined);
      } else {
        onChangeReferralSourceId(value === selectedReferralSourceId ? undefined : value);
      }
    },
    [selectedReferralSourceId, onChangeReferralSourceId],
  );

  const handleAddSource = useCallback(() => {
    if (!newSourceName.trim()) return;

    const existingSource = referralSources.find(
      source => source.referralSourceLabel.toLowerCase() === newSourceName.toLowerCase(),
    );

    if (existingSource) {
      onChangeReferralSourceId(existingSource.advisorReferralSourceId);
      setIsAddingMode(false);
      setNewSourceName('');
      return;
    }

    const newSource = {
      referralSourceLabel: newSourceName,
      advisorReferralSourceId: newUuid(),
    } as AdvisorReferralSource;

    onChangeReferralSourceId(newSource.advisorReferralSourceId);

    dispatch(
      putAdvisorReferralSourceByAdvisorIdRequest({
        advisorReferralSourceId: newSource.advisorReferralSourceId,
        referralSourceLabel: newSource.referralSourceLabel,
        advisorId: advisor.advisorId,
      }),
    );

    setReferralSources(prev =>
      _.sortBy([...prev, newSource], source => source.referralSourceLabel.toLowerCase().trim()),
    );
    setIsAddingMode(false);
    setNewSourceName('');
  }, [newSourceName, referralSources, onChangeReferralSourceId, dispatch, advisor.advisorId]);

  const handleCancel = useCallback(() => {
    setIsAddingMode(false);
    setNewSourceName('');
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <Label>Referral Source</Label>
      <Select
        onValueChange={handleValueChange}
        value={selectedReferralSourceId ?? 'undefined'}
      >
        <SelectTrigger
          className={cn('w-full', className)}
          disabled={isLoading}
        >
          <SelectValue>
            {selectedSource?.referralSourceLabel ?? 'Select referral source'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="z-100 max-h-100">
          <SelectGroup>
            <div className="-mx-1 mb-1 border-b px-1 pb-1">
              {isAddingMode ? (
                <div className="flex flex-col gap-2 p-2">
                  <Input
                    value={newSourceName}
                    onChange={e => setNewSourceName(e.target.value)}
                    placeholder="Enter new source..."
                    autoFocus
                    disabled={isLoading}
                    onKeyDown={e => {
                      e.stopPropagation();
                      if (e.key === 'Enter') handleAddSource();
                      if (e.key === 'Escape') handleCancel();
                    }}
                    className="rounded-sm"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleCancel}
                      disabled={isLoading}
                      className="shrink-0 grow rounded-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleAddSource}
                      disabled={isLoading}
                      className="shrink-0 grow rounded-sm"
                    >
                      {isLoading ? 'Adding...' : 'Add'}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  className="w-full justify-start rounded-sm text-start font-normal"
                  onClick={() => setIsAddingMode(true)}
                >
                  <Plus />
                  Add New Referral Source
                </Button>
              )}
            </div>
            <SelectItem value="undefined">None</SelectItem>
            {referralSources.map(source => (
              <SelectItem
                key={source.advisorReferralSourceId}
                value={source.advisorReferralSourceId}
              >
                {source.referralSourceLabel}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}
